// The seals of one side: one for each lasting effect that binds it, in the
// head of its team sheet. A seal is the icon of the spell on a disc of its
// polarity, inside a ring with one segment for each turn. A spent segment is
// dotted. The seal has no digit: the digits of the game are energy and costs.
// A seal comes on the beat of the cast, loses a segment on the beat of each
// turn, and thins through half and quarter dither on the beat of the end, as
// a cloud does. It moves between those moments only when a coin of energy
// pings off it. `game/lasting.ts` is the table. Added 2026-10-14 from a design
// pass. Tick marks around the disc were drawn first and read as a pause icon.

import type { Side } from '../engine/types';
import { type LastingKey, type LastingOn, lastingOn } from '../game/lasting';
import { el } from '../lib/dom';
import { svg } from '../lib/svg';
import { spellById } from '../spells/library';
import { isReviewing } from '../state/history';
import { shownLasting, subscribeLasting } from '../state/lastingShown';
import { createSpellIcon } from './SpellIcon';

/** The ring in the units of its view box: the radius of its line, and the width of the line. */
const RING_RADIUS = 14;
const RING_WIDTH = 3;
const RING_BOX = (RING_RADIUS + RING_WIDTH) * 2;
/** The gap between two segments, in degrees. */
const SEGMENT_GAP_DEGREES = 26;
const FULL_DEGREES = 360;
const ENDING_CLASS = 'seal--ending';
/** The name of the keyframes of `seal--ending` in `lasting.css`. */
const THIN_ANIMATION = 'seal-thin';
const PINGED_CLASS = 'seal--pinged';

/** A point on the ring, clockwise from the top. */
const ringPoint = (degrees: number): string => {
  const radians = (degrees * Math.PI) / (FULL_DEGREES / 2);
  return `${(Math.sin(radians) * RING_RADIUS).toFixed(2)} ${(-Math.cos(radians) * RING_RADIUS).toFixed(2)}`;
};

/** One segment of the ring, as a path of one arc. */
const segmentPath = (index: number, total: number): string => {
  const step = FULL_DEGREES / total;
  const gap = total === 1 ? 0 : SEGMENT_GAP_DEGREES / 2;
  const from = index * step + gap;
  // A ring of one segment is two arcs, because one arc cannot close on itself.
  if (total === 1)
    return `M${ringPoint(0)} A${RING_RADIUS} ${RING_RADIUS} 0 1 1 ${ringPoint(180)} A${RING_RADIUS} ${RING_RADIUS} 0 1 1 ${ringPoint(0)}`;
  const to = (index + 1) * step - gap;
  const large = to - from > FULL_DEGREES / 2 ? 1 : 0;
  return `M${ringPoint(from)} A${RING_RADIUS} ${RING_RADIUS} 0 ${large} 1 ${ringPoint(to)}`;
};

type Seal = { root: HTMLElement; ring: SVGSVGElement; total: number };

const createSeal = (on: LastingOn): Seal => {
  const { lasting } = on;
  const ring = svg('svg', {
    class: 'seal__ring',
    viewBox: `${-RING_BOX / 2} ${-RING_BOX / 2} ${RING_BOX} ${RING_BOX}`,
    'aria-hidden': 'true',
  });
  const root = el('span', { className: `seal seal--${lasting.polarity}` }, [
    ring,
    el('span', { className: 'seal__disc' }, [createSpellIcon(lasting.spellId, 'seal__icon')]),
  ]);
  root.dataset.tipSpell = lasting.spellId;
  root.dataset.tipTitle = spellById(lasting.spellId).name;
  return { root, ring, total: 0 };
};

/** Draws the segments. A segment that a turn spends goes faint by CSS, so the change shows. */
const drawRing = (seal: Seal, on: LastingOn): void => {
  if (seal.total !== on.total) {
    seal.ring.replaceChildren(
      ...Array.from({ length: on.total }, (_, index) =>
        svg('path', { class: 'seal__segment', d: segmentPath(index, on.total) }),
      ),
    );
    seal.total = on.total;
  }
  [...seal.ring.children].forEach((segment, index) => {
    segment.classList.toggle('seal__segment--spent', index >= on.left);
  });
  seal.root.dataset.tip = on.lasting.describe(on.left);
  seal.root.ariaLabel = `${spellById(on.lasting.spellId).name}. ${on.lasting.describe(on.left)}`;
};

/** For each row of seals on the page, the last seal and its box, or null when the row shows none. */
type SealFinder = () => { element: HTMLElement; box: DOMRect } | null;
const sealRows: Record<Side, SealFinder[]> = { gold: [], violet: [] };

const firstSeal = (side: Side): ReturnType<SealFinder> => {
  for (const find of sealRows[side]) {
    const found = find();
    if (found) return found;
  }
  return null;
};

/**
 * The box of the seal that a coin of `side` hits, when a seal covers the
 * coins of that side: the line of Moon on the phone. Null when no seal shows.
 */
export const sealTarget = (side: Side): DOMRect | null => firstSeal(side)?.box ?? null;

/** The seal a coin hit jolts. Garrett, 2026-10-14: "the coin to fly and then ping off the sealed thing". */
export const pingSeal = (side: Side): void => {
  const seal = firstSeal(side)?.element;
  if (!seal) return;
  seal.classList.remove(PINGED_CLASS);
  void seal.offsetWidth;
  seal.classList.add(PINGED_CLASS);
};

export const createLastingSeals = (side: Side): HTMLElement => {
  const root = el('span', { className: 'seals' });
  const seals = new Map<LastingKey, Seal>();

  sealRows[side].push(() => {
    if (!root.isConnected) return null;
    const last = [...seals.values()].at(-1)?.root;
    const box = last?.getBoundingClientRect();
    return last && box && box.width > 0 ? { element: last, box } : null;
  });

  const draw = (): void => {
    const quiet = isReviewing();
    const now = lastingOn(shownLasting(), side);
    const keys = new Set(now.map((on) => on.lasting.key));
    for (const [key, seal] of seals) {
      if (keys.has(key)) continue;
      seals.delete(key);
      if (quiet) {
        seal.root.remove();
        continue;
      }
      // The seal thins as a cloud does, then goes. The CSS has the steps.
      seal.root.classList.add(ENDING_CLASS);
      seal.root.addEventListener('animationend', (event) => {
        if (event.animationName === THIN_ANIMATION) seal.root.remove();
      });
    }
    for (const on of now) {
      let seal = seals.get(on.lasting.key);
      if (!seal) {
        seal = createSeal(on);
        seals.set(on.lasting.key, seal);
        seal.root.classList.toggle('seal--fresh', !quiet);
        root.append(seal.root);
      }
      drawRing(seal, on);
    }
    // A seal that ends keeps its place until it is gone, so the head does not jump twice.
    root.classList.toggle('seals--bound', now.length > 0);
  };

  draw();
  subscribeLasting(draw);
  return root;
};
